import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CButton, CFormInput, CFormLabel, CRow, CCol } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilMinus, cilX, cilMove } from '@coreui/icons';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  getOddsConverterVisible,
  getOddsConverterMinimized,
  getOddsConverterPosition,
  hideOddsConverter,
  toggleMinimized,
  setPosition
} from 'redux/slices/odds-converter-slice';
import { convertFromDecimal, formatAmericanOdds } from 'utils/oddsConverter';
import './OddsConverter.scss';

const OddsConverter: React.FC = () => {
  const dispatch = useAppDispatch();
  const visible = useAppSelector(getOddsConverterVisible);
  const minimized = useAppSelector(getOddsConverterMinimized);
  const position = useAppSelector(getOddsConverterPosition);
  const windowRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const [decimalOdds, setDecimalOdds] = useState<string>('2.00');
  const [americanOdds, setAmericanOdds] = useState<string>('+100');
  const [fractionalOdds, setFractionalOdds] = useState<string>('1/1');
  const [impliedProbability, setImpliedProbability] = useState<string>('50.00');
  const [activeInput, setActiveInput] = useState<string>('decimal');

  // Convert from decimal odds
  const fromDecimal = (decimal: number) => {
    if (decimal <= 1 || isNaN(decimal)) return;
    
    try {
      const converted = convertFromDecimal(decimal);
      setAmericanOdds(formatAmericanOdds(converted.american));
      setFractionalOdds(converted.fractional);
      setImpliedProbability(converted.percentage.toFixed(2));
    } catch (error) {
      console.error('Error converting decimal odds:', error);
    }
  };

  // Convert from American odds
  const fromAmerican = (american: string) => {
    const value = parseFloat(american.replace('+', ''));
    if (isNaN(value)) return;

    let decimal: number;
    if (value > 0) {
      decimal = (value / 100) + 1;
    } else {
      decimal = (100 / Math.abs(value)) + 1;
    }

    setDecimalOdds(decimal.toFixed(2));

    // Update other fields WITHOUT recalculating American odds
    try {
      const converted = convertFromDecimal(decimal);
      setFractionalOdds(converted.fractional);
      setImpliedProbability(converted.percentage.toFixed(2));
    } catch (error) {
      console.error('Error converting decimal odds:', error);
    }
  };

  // Convert from fractional odds
  const fromFractional = (fractional: string) => {
    const parts = fractional.split('/');
    if (parts.length !== 2) return;

    const numerator = parseFloat(parts[0]);
    const denominator = parseFloat(parts[1]);
    if (isNaN(numerator) || isNaN(denominator) || denominator === 0) return;

    const decimal = (numerator / denominator) + 1;

    setDecimalOdds(decimal.toFixed(2));

    // Update other fields WITHOUT recalculating Fractional odds
    try {
      const converted = convertFromDecimal(decimal);
      setAmericanOdds(formatAmericanOdds(converted.american));
      setImpliedProbability(converted.percentage.toFixed(2));
    } catch (error) {
      console.error('Error converting decimal odds:', error);
    }
  };

  // Convert from implied probability
  const fromProbability = (probability: string) => {
    const prob = parseFloat(probability);
    if (isNaN(prob) || prob <= 0 || prob >= 100) return;

    const decimal = 100 / prob;

    setDecimalOdds(decimal.toFixed(2));

    // Update other fields WITHOUT recalculating Probability
    try {
      const converted = convertFromDecimal(decimal);
      setAmericanOdds(formatAmericanOdds(converted.american));
      setFractionalOdds(converted.fractional);
    } catch (error) {
      console.error('Error converting decimal odds:', error);
    }
  };


  const handleDecimalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDecimalOdds(value);
    setActiveInput('decimal');
    const decimal = parseFloat(value);
    if (!isNaN(decimal) && decimal > 1) {
      fromDecimal(decimal);
    }
  };

  const handleAmericanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Ensure + or - prefix
    if (value && !value.startsWith('+') && !value.startsWith('-')) {
      value = '+' + value;
    }
    setAmericanOdds(value);
    setActiveInput('american');
    fromAmerican(value);
  };

  const handleFractionalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFractionalOdds(value);
    setActiveInput('fractional');
    fromFractional(value);
  };

  const handleProbabilityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setImpliedProbability(value);
    setActiveInput('probability');
    fromProbability(value);
  };

  // Dragging functionality
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!windowRef.current) return;
    
    isDraggingRef.current = true;
    const rect = windowRef.current.getBoundingClientRect();
    dragOffsetRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current) return;
    
    const newX = e.clientX - dragOffsetRef.current.x;
    const newY = e.clientY - dragOffsetRef.current.y;
    
    // Keep window within viewport bounds
    const maxX = window.innerWidth - 400; // window width
    const maxY = window.innerHeight - 200; // minimum height
    
    const boundedX = Math.max(0, Math.min(newX, maxX));
    const boundedY = Math.max(0, Math.min(newY, maxY));
    
    dispatch(setPosition({ x: boundedX, y: boundedY }));
  }, [dispatch]);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  // Cleanup event listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  if (!visible) return null;


  return (
    <div 
      ref={windowRef}
      className={`odds-converter-floating ${minimized ? 'minimized' : ''}`}
      style={{ 
        left: `${position.x}px`, 
        top: `${position.y}px` 
      }}
    >
      <div className="odds-converter-header" onMouseDown={handleMouseDown}>
        <div className="header-content">
          <CIcon icon={cilMove} className="drag-icon" />
          <span className="title">Odds Converter</span>
        </div>
        <div className="header-controls">
          <CButton 
            color="secondary" 
            variant="ghost" 
            size="sm"
            onClick={() => dispatch(toggleMinimized())}
          >
            <CIcon icon={cilMinus} />
          </CButton>
          <CButton 
            color="danger" 
            variant="ghost" 
            size="sm"
            onClick={() => dispatch(hideOddsConverter())}
          >
            <CIcon icon={cilX} />
          </CButton>
        </div>
      </div>
      
      {!minimized && (
        <div className="odds-converter-content">
          
          <CRow>
            <CCol md={6} className="mb-3">
              <CFormLabel htmlFor="decimal">Decimal Odds</CFormLabel>
              <CFormInput
                id="decimal"
                type="text"
                value={decimalOdds}
                onChange={handleDecimalChange}
                placeholder="e.g., 2.00"
                className={activeInput === 'decimal' ? 'active-input' : ''}
              />
            </CCol>
            
            <CCol md={6} className="mb-3">
              <CFormLabel htmlFor="american">American Odds</CFormLabel>
              <CFormInput
                id="american"
                type="text"
                value={americanOdds}
                onChange={handleAmericanChange}
                placeholder="e.g., +100 or -150"
                className={activeInput === 'american' ? 'active-input' : ''}
              />
            </CCol>
            
            <CCol md={6} className="mb-3">
              <CFormLabel htmlFor="fractional">Fractional Odds</CFormLabel>
              <CFormInput
                id="fractional"
                type="text"
                value={fractionalOdds}
                onChange={handleFractionalChange}
                placeholder="e.g., 3/2"
                className={activeInput === 'fractional' ? 'active-input' : ''}
              />
            </CCol>
            
            <CCol md={6} className="mb-3">
              <CFormLabel htmlFor="probability">Implied Probability (%)</CFormLabel>
              <CFormInput
                id="probability"
                type="text"
                value={impliedProbability}
                onChange={handleProbabilityChange}
                placeholder="e.g., 50.00"
                className={activeInput === 'probability' ? 'active-input' : ''}
              />
            </CCol>
          </CRow>
        </div>
      )}
    </div>
  );
};

export default OddsConverter;